import React, { useState, useEffect } from "react";
import { FaBell, FaChartPie } from "react-icons/fa";
import axios from "../../services/axios";
import { MdPendingActions } from "react-icons/md";
import {
  FileDoneOutlined,
  SolutionOutlined,
  SendOutlined,
  ExclamationCircleFilled,
  SafetyCertificateFilled,
  ProfileOutlined,
} from "@ant-design/icons";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell,
  PieChart,
  Pie,
} from "recharts";

const Dashboard = () => {
  const [notifications, setNotifications] = useState([]);
  const user = localStorage.getItem("user");
  const projectName = JSON.parse(localStorage.getItem("Project"));
  const [pendingTasks, setPendingTasks] = useState(0);
  const [totalDocuments, setTotalDocuments] = useState(0);
  const [totalDocumentsApproved, setTotalDocumentsApproved] = useState(0);
  const [totalDocumentsRejected, setTotalDocumentsRejected] = useState(0);
  const role = JSON.parse(localStorage.getItem("role"))[0];
  const [activeTab, setActiveTab] = useState("overview");
  const [isLoading, setIsLoading] = useState(true);

  const documentData = [
    { name: "Uploaded", value: totalDocuments, color: "#3B82F6" },
    { name: "Approved", value: totalDocumentsApproved, color: "#10B981" },
    { name: "Rejected", value: totalDocumentsRejected, color: "#EF4444" },
    { name: "Pending", value: pendingTasks, color: "#F59E0B" },
    { name: "Under Internal Review", value: 0, color: "#8B5CF6" },
    { name: "Under Review of Engineer", value: 0, color: "#4F46E5" },
    { name: "Transmitted for Approval", value: 0, color: "#0EA5E9" },
  ];

  const documentsPieData = [
    { name: "Uploaded", value: totalDocuments, color: "#3B82F6" },
    { name: "Approved", value: totalDocumentsApproved, color: "#10B981" },
    { name: "Rejected", value: totalDocumentsRejected, color: "#EF4444" },
  ];

  const pendingPieData = [
    { name: "Pending", value: pendingTasks, color: "#F59E0B" },
    { name: "Others", value: totalDocuments - pendingTasks, color: "#D1D5DB" },
  ];

  const statusPieData = [
    { name: "Internal Review", value: 0, color: "#8B5CF6" },
    { name: "Engineer Review", value: 0, color: "#4F46E5" },
    { name: "Transmitted", value: 0, color: "#0EA5E9" },
  ];

  useEffect(() => {
    const fetchNotifications = async () => {
      try {
        const response = await axios.post("get-user-notifications", { user, projectName });
        setNotifications(response.data.notifications.slice(0, 3));
      } catch (error) {
        console.error("Error fetching notifications:", error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchNotifications();
  }, [user, projectName]);

  useEffect(() => {
    const fetchPendingTasks = async () => {
      try {
        const response = await axios.post("document-count", { user, role, projectName });
        setPendingTasks(response.data.pendingCount);
      } catch (error) {
        console.error("Error fetching pending tasks:", error);
      }
    };
    fetchPendingTasks();
  }, [user, projectName]);

  useEffect(() => {
    const fetchTotalDocuments = async () => {
      try {
        const response = await axios.post("get-total-docuemnts-uploaded", { user, role, projectName });
        setTotalDocuments(response.data.totalDocuments);
      } catch (error) {
        console.error("Error fetching total documents:", error);
      }
    };
    fetchTotalDocuments();
  }, [user, role, projectName]);

  useEffect(() => {
    const fetchTotalDocumentsApproved = async () => {
      try {
        const response = await axios.post("get-docuemnts-approved", { user, role, projectName });
        setTotalDocumentsApproved(response.data.totalDocumentsApproved);
      } catch (error) {
        console.error("Error fetching total documents:", error);
      }
    };
    fetchTotalDocumentsApproved();
  }, [user, role, projectName]);

  useEffect(() => {
    const fetchTotalDocumentsRejected = async () => {
      try {
        const response = await axios.post("get-docuemnts-rejected", { user, role, projectName });
        setTotalDocumentsRejected(response.data.totalDocumentsRejected);
      } catch (error) {
        console.error("Error fetching total documents:", error);
      }
    };
    fetchTotalDocumentsRejected();
  }, [user, role, projectName]);

  if (isLoading) {
    return (
      <div className="relative flex-1 p-6 overflow-y-auto ml-7 mr-2 min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-blue-500 mx-auto mb-4"></div>
          <h3 className="text-xl font-semibold text-gray-700">Loading dashboard data...</h3>
        </div>
      </div>
    );
  }

  return (
    <div className="relative flex-1 p-6 overflow-y-auto ml-7 mr-2 min-h-screen bg-gray-50">
      {/* Navigation Tabs */}
      <div className="flex mb-6 bg-white rounded-lg shadow overflow-x-auto">
        <button
          className={`px-4 py-3 flex items-center ${
            activeTab === "overview"
              ? "text-blue-600 border-b-2 border-blue-600 font-semibold"
              : "text-gray-600"
          }`}
          onClick={() => setActiveTab("overview")}
        >
          <MdPendingActions className="mr-2" /> Overview
        </button>
        <button
          className={`px-4 py-3 flex items-center ${
            activeTab === "charts"
              ? "text-blue-600 border-b-2 border-blue-600 font-semibold"
              : "text-gray-600"
          }`}
          onClick={() => setActiveTab("charts")}
        >
          <FaChartPie className="mr-2" /> Charts
        </button>
      </div>

      {/* Overview Tab */}
      {activeTab === "overview" && (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
            <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-lg shadow p-6 text-white">
              <div className="flex justify-between items-start">
                <div>
                  <p className="text-blue-100">Total Documents Uploaded</p>
                  <h3 className="text-3xl font-bold mt-1">{totalDocuments}</h3>
                </div>
                <div className="p-3 bg-blue-400 bg-opacity-30 rounded-full">
                  <FileDoneOutlined className="text-2xl" />
                </div>
              </div>
            </div>
            <div className="bg-gradient-to-r from-green-500 to-green-600 rounded-lg shadow p-6 text-white">
              <div className="flex justify-between items-start">
                <div>
                  <p className="text-green-100">Total Documents Approved</p>
                  <h3 className="text-3xl font-bold mt-1">{totalDocumentsApproved}</h3>
                </div>
                <div className="p-3 bg-green-400 bg-opacity-30 rounded-full">
                  <SafetyCertificateFilled className="text-2xl" />
                </div>
              </div>
            </div>
            <div className="bg-gradient-to-r from-red-500 to-red-600 rounded-lg shadow p-6 text-white">
              <div className="flex justify-between items-start">
                <div>
                  <p className="text-red-100">Total Documents Rejected</p>
                  <h3 className="text-3xl font-bold mt-1">{totalDocumentsRejected}</h3>
                </div>
                <div className="p-3 bg-red-400 bg-opacity-30 rounded-full">
                  <ExclamationCircleFilled className="text-2xl" />
                </div>
              </div>
            </div>
            <div className="bg-gradient-to-r from-yellow-500 to-yellow-600 rounded-lg shadow p-6 text-white">
              <div className="flex justify-between items-start">
                <div>
                  <p className="text-yellow-100">Pending Tasks</p>
                  <h3 className="text-3xl font-bold mt-1">{pendingTasks}</h3>
                </div>
                <div className="p-3 bg-yellow-400 bg-opacity-30 rounded-full">
                  <MdPendingActions className="text-2xl" />
                </div>
              </div>
            </div>
          </div>

          {/* Chart and Notifications */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
            <div className="lg:col-span-2 bg-white shadow rounded-lg p-6">
              <h2 className="text-xl font-semibold text-gray-800 mb-4">Document Summary</h2>
              <ResponsiveContainer width="100%" height={50 * documentData.length}>
                <BarChart
                  layout="vertical"
                  data={documentData}
                  margin={{ top: 10, right: 30, left: 60, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis
                    type="number"
                    tick={{ fill: "#4B5563", fontWeight: 500, fontSize: 12 }}
                  />
                  <YAxis
                    dataKey="name"
                    type="category"
                    tick={{ fill: "#4B5563", fontWeight: 500, fontSize: 12 }}
                    width={120}
                  />
                  <Tooltip />
                  <Legend verticalAlign="top" height={36} />
                  <Bar dataKey="value" radius={[0, 6, 6, 0]}>
                    {documentData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="bg-white shadow rounded-lg p-6">
              <h2 className="text-xl font-semibold text-gray-800 mb-4">Recent Notifications</h2>
              {notifications.length > 0 ? (
                <ul className="space-y-4">
                  {notifications.map((notif, index) => (
                    <li key={index} className="flex items-start p-3 bg-blue-50 rounded-lg">
                      <div className="bg-blue-100 p-2 rounded-full mr-3">
                        <FaBell className="text-blue-500" />
                      </div>
                      <div>
                        <p className="text-gray-800">{notif.message}</p>
                        <p className="text-xs text-gray-500 mt-1">
                          {notif.timestamp ? new Date(notif.timestamp).toLocaleString() : "Recently"}
                        </p>
                      </div>
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="flex flex-col items-center justify-center h-56 text-gray-500">
                  <FaBell className="text-4xl mb-2" />
                  <p>No new notifications</p>
                </div>
              )}
            </div>
          </div>

          {/* Status Details */}
          <h2 className="text-2xl font-semibold text-gray-800 mb-4">Document Status Details</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-white shadow rounded-lg p-6">
              <div className="flex items-center mb-4">
                <div className="p-2 bg-blue-100 rounded-full mr-3">
                  <SolutionOutlined className="text-blue-600 text-2xl" />
                </div>
                <h3 className="text-xl font-semibold text-gray-800">Under Internal Review</h3>
              </div>
              <div className="bg-blue-50 p-4 rounded-lg">
                <p className="text-lg font-medium text-gray-800">Status</p>
                <p className="text-gray-600">3 projects in progress</p>
              </div>
            </div>
            <div className="bg-white shadow rounded-lg p-6">
              <div className="flex items-center mb-4">
                <div className="p-2 bg-yellow-100 rounded-full mr-3">
                  <SendOutlined className="text-yellow-600 text-2xl" />
                </div>
                <h3 className="text-xl font-semibold text-gray-800">Transmitted for Approval</h3>
              </div>
              <div className="bg-yellow-50 p-4 rounded-lg">
                <p className="text-lg font-medium text-gray-800">Status</p>
                <p className="text-gray-600">3 projects in progress</p>
              </div>
            </div>
            <div className="bg-white shadow rounded-lg p-6">
              <div className="flex items-center mb-4">
                <div className="p-2 bg-indigo-100 rounded-full mr-3">
                  <ProfileOutlined className="text-indigo-600 text-2xl" />
                </div>
                <h3 className="text-xl font-semibold text-gray-800">Under Review of Engineer</h3>
              </div>
              <div className="bg-indigo-50 p-4 rounded-lg">
                <p className="text-lg font-medium text-gray-800">Status</p>
                <p className="text-gray-600">3 projects in progress</p>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Charts Tab */}
      {activeTab === "charts" && (
        <>
          <h2 className="text-2xl font-semibold text-gray-800 mb-4">Comprehensive Charts</h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-xl font-semibold text-gray-800 mb-4">Documents Distribution</h3>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={documentsPieData}
                    dataKey="value"
                    cx="50%"
                    cy="50%"
                    outerRadius={100}
                    labelLine={false}
                    label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                  >
                    {documentsPieData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-xl font-semibold text-gray-800 mb-4">Pending Tasks</h3>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={pendingPieData}
                    dataKey="value"
                    cx="50%"
                    cy="50%"
                    outerRadius={100}
                    labelLine={false}
                    label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                  >
                    {pendingPieData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-xl font-semibold text-gray-800 mb-4">Status Breakdown</h3>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={statusPieData}
                    dataKey="value"
                    cx="50%"
                    cy="50%"
                    outerRadius={100}
                    labelLine={false}
                    label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                  >
                    {statusPieData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-xl font-semibold text-gray-800 mb-4">Document Summary</h3>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart
                  layout="vertical"
                  data={documentData}
                  margin={{ top: 10, right: 30, left: 60, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis
                    type="number"
                    tick={{ fill: "#4B5563", fontWeight: 500, fontSize: 12 }}
                  />
                  <YAxis
                    dataKey="name"
                    type="category"
                    tick={{ fill: "#4B5563", fontWeight: 500, fontSize: 12 }}
                    width={120}
                  />
                  <Tooltip />
                  <Legend verticalAlign="top" height={36} />
                  <Bar dataKey="value" radius={[0, 6, 6, 0]}>
                    {documentData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Status Table */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-xl font-semibold text-gray-800 mb-4">Document Status Summary</h3>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Document Type
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Total Uploaded
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Approved
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Rejected
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Pending
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Approval Rate
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  <tr>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      Documents
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {totalDocuments}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-green-600 font-medium">
                      {totalDocumentsApproved}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-red-600 font-medium">
                      {totalDocumentsRejected}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-yellow-600 font-medium">
                      {pendingTasks}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {totalDocuments > 0
                        ? `${Math.round((totalDocumentsApproved / totalDocuments) * 100)}%`
                        : "0%"}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Dashboard;